'use client';

import { USER_TYPE } from '@/app/(auth)/constants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { env } from '@/env';
import { BRANDING_DATA_KEY } from '@/hooks/use-current-branding';
import { cn } from '@/lib/cn';
import { writeToLocalStorage } from '@/lib/local-storage/local-storage';
import { useStoredSessions, type StoredSession } from '@/lib/sessions/storage';
import type { SessionValues } from '@/providers/session-provider/types';
import { getSessionValues } from '@/providers/session-provider/get-session-values';
import { setSessionValues } from '@/providers/session-provider/set-session-values';
import { BASE_BACKEND_URL } from '@/requests/constants';
import { useQuery } from '@tanstack/react-query';
import { LogInIcon } from 'lucide-react';
import { Icons } from '@/components/icons';
import type { ComponentProps } from 'react';

interface SessionProfile {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  profile_picture?: { get?: string } | null;
}

// Fetches the profile for a specific stored session by setting that session's
// token manually, independent of the currently active session.
async function fetchSessionProfile(
  session: SessionValues
): Promise<SessionProfile> {
  const res = await fetch(`${BASE_BACKEND_URL}/accounts/users/profile/`, {
    headers: {
      Authorization: `Token ${session.token}`,
      'X-PIGELLO-USER-ID': session.userId,
      'X-PIGELLO-CUSTOMER-ID': session.customerId,
      'X-PIGELLO-CLIENT-TYPE': `${env.NEXT_PUBLIC_CLIENT_TYPE},frontend,1.0.0`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const error = new Error('Failed to fetch session profile') as Error & {
      status: number;
    };
    error.status = res.status;
    throw error;
  }
  return res.json();
}

function redirectToLogin() {
  const url = new URL(env.NEXT_PUBLIC_BASE_LOGIN_URL);

  // We might be on a branded domain, like customer.app.pigello.io. Since we're potentially logging into another customer,
  // swap for the generic app.pigello.io domain so that we don't get any branding by default.
  const host =
    env.NEXT_PUBLIC_CLIENT_TYPE === 'prod'
      ? 'https://app.pigello.io'
      : 'https://demo.pigello.io';
  const redirectUrl = new URL('/login/authenticate', host);

  url.searchParams.set('user_type', USER_TYPE);
  url.searchParams.set('redirect_url', redirectUrl.toString());

  window.location.replace(url.toString());
}

function setActiveSession(stored: StoredSession) {
  const { session, branding } = stored;
  setSessionValues({
    token: session.token,
    tokenExp: session.tokenExp,
    userId: session.userId,
    organizationId: session.organizationId,
    customerId: session.customerId,
    restrictedToIpAddress: session.restrictedToIpAddress,
    segments: session.segments,
  });
  // Apply the branding captured for this account so the UI matches it.
  writeToLocalStorage({ key: BRANDING_DATA_KEY, value: branding });
  // Full reload so the app re-fetches everything as the newly active user.
  window.location.assign('/');
}

function SessionCard({
  stored,
  isActive,
  onDelete,
}: {
  stored: StoredSession;
  isActive: boolean;
  onDelete: () => void;
}) {
  const { session, branding } = stored;
  const { data: profile, error: profileError } = useQuery({
    queryKey: ['stored-session-profile', session.token],
    queryFn: () => fetchSessionProfile(session),
    staleTime: 60 * 1000,
    retry: false,
    // A 401 here is expected for an expired stored token and must not trigger
    // the global "log the active user out" behaviour.
    meta: { skipGlobalLogoutOn401: true },
  });

  // A 401 means this session's token is no longer valid (expired/inactive),
  // so there's nothing to switch to — hide it.
  if ((profileError as { status?: number } | null)?.status === 401) {
    return null;
  }

  const fullName =
    profile?.full_name?.trim() ||
    `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim() ||
    session.userId;
  const initials =
    `${profile?.first_name?.[0] ?? ''}${profile?.last_name?.[0] ?? ''}`.toUpperCase();
  // Use the branding stored with the session (the exact branding it was saved
  // with) for display.
  const brandingName = branding?.branding?.name;
  const primaryColor = branding?.branding?.colors?.primary;

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md border pr-2 transition-colors',
        !isActive && 'hover:bg-accent'
      )}
    >
      <button
        type='button'
        disabled={isActive}
        onClick={isActive ? undefined : () => setActiveSession(stored)}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 p-3 text-left',
          isActive && 'cursor-default'
        )}
      >
        <Avatar className='size-8'>
          <AvatarImage src={profile?.profile_picture?.get} alt={fullName} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className='flex min-w-0 flex-col'>
          <span className='flex items-center gap-2'>
            {brandingName ? (
              <span className='flex min-w-0 items-center gap-1.5 text-sm font-medium'>
                {primaryColor && (
                  <span
                    className='size-3 shrink-0 rounded-sm'
                    style={{ backgroundColor: primaryColor }}
                  />
                )}
                <span className='truncate'>{brandingName}</span>
              </span>
            ) : (
              <span className='truncate text-sm font-medium'>{fullName}</span>
            )}
            {isActive && <Badge variant='blue'>Nuvarande konto</Badge>}
          </span>
          {brandingName && (
            <span className='truncate text-xs text-muted-foreground'>
              {fullName}
            </span>
          )}
        </div>
      </button>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        className='shrink-0 text-muted-foreground hover:text-destructive'
        onClick={onDelete}
        aria-label='Ta bort konto'
      >
        <Icons.delete className='size-4' />
      </Button>
    </div>
  );
}

export function SwitchAccountModal(props: ComponentProps<typeof Dialog>) {
  const { sessions, removeSession } = useStoredSessions();
  const activeToken = getSessionValues().token;

  return (
    <Dialog {...props}>
      <DialogContent className='p-0'>
        <DialogHeader>
          <div className='flex flex-col gap-1'>
            <DialogTitle>Byt konto</DialogTitle>
            <DialogDescription>
              Alla aktiva sessioner på den här enheten visas. En session är
              aktiv i 6 timmar. Aktivera &quot;Tillåt flera sessioner&quot; för
              kontot om du vill vara inloggad på flera enheter samtidigt.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className='grid gap-2 p-4'>
          {sessions.map((stored) => (
            <SessionCard
              key={stored.session.token}
              stored={stored}
              isActive={stored.session.token === activeToken}
              onDelete={() => removeSession(stored)}
            />
          ))}
          <Button
            variant='outline'
            className='w-full'
            onClick={redirectToLogin}
          >
            <LogInIcon className='mr-2 size-4' />
            Logga in på annat konto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
